import React, { Suspense, useEffect, useRef, lazy } from 'react';
import {
  BrowserRouter as Router,
  Navigate,
  Routes,
  Route,
  useLocation,
  useNavigationType,
  useParams,
} from 'react-router-dom';
import ReferralCapture from './components/ReferralCapture';
import { captureAcquisition } from './utils/acquisition';
import { releaseStaticShell, removeCrawlerShell } from './seo/guideShell';
import Home from './components/Home';
import Navbar from './components/Navbar';
import DeferredBoot from './components/DeferredBoot';
import LoadPhi from './components/LoadPhi';
import { QuizProvider } from './components/QuizContext';
import RequireAdmin from './components/RequireAdmin';
import RequireAuth from './components/RequireAuth';

const AntdProvider = lazy(() => import('./components/AntdProvider'));
const AnalyticsNotice = lazy(() => import('./components/AnalyticsNotice'));
const VerifyEmailNotice = lazy(() => import('./components/VerifyEmailNotice'));
const FunnelFeedback = lazy(() => import('./components/FunnelFeedback'));
const FoundingOfferPromo = lazy(() => import('./components/FoundingOfferPromo'));
const GoogleAuthComplete = lazy(() => import('./components/GoogleAuthComplete'));
const Quiz = lazy(() => import('./components/Quiz'));
const SignUp = lazy(() => import('./components/SignUp'));
const LoginPage = lazy(() => import('./components/LoginPage'));
const ForgotPassword = lazy(() => import('./components/ForgotPassword'));
const Glossary = lazy(() => import('./components/Glossary'));
const Leaderboard = lazy(() => import('./components/Leaderboard'));
const CountryPage = lazy(() => import('./components/CountryPage'));
const MockExamsHub = lazy(() => import('./components/MockExamsHub'));
const GuidePage = lazy(() => import('./components/GuidePage'));
const Blog = lazy(() => import('./components/Blog'));
const StudyGuide = lazy(() => import('./components/StudyGuide'));
const NotFoundPage = lazy(() => import('./components/NotFoundPage'));
const EditProfile = lazy(() => import('./components/EditProfile'));
const QuizAttempts = lazy(() => import('./components/quizAttempts'));
const Progress = lazy(() => import('./components/Progress'));
const ExamDateOnboarding = lazy(() => import('./components/ExamDateOnboarding'));
const Privacy = lazy(() => import('./components/Legal').then((m) => ({ default: m.Privacy })));
const ReferralTerms = lazy(() => import('./components/ReferralTerms'));
const Terms = lazy(() => import('./components/Legal').then((m) => ({ default: m.Terms })));
const Careers = lazy(() => import('./components/Careers'));
const StudyPlan = lazy(() => import('./components/StudyPlan'));
const About = lazy(() => import('./components/Company').then((m) => ({ default: m.About })));
const Contact = lazy(() => import('./components/Company').then((m) => ({ default: m.Contact })));
const PhiIdentify = lazy(() => import('./components/PhiIdentify'));
const Presence = lazy(() => import('./components/Presence'));
const PageViewTracker = lazy(() => import('./components/PageViewTracker'));
const EmailDrillResult = lazy(() => import('./components/EmailDrillResult'));
const ReviewAttempt = lazy(() => import('./components/ReviewAttempt'));
const Pricing = lazy(() => import('./components/Pricing'));
const LevelHub = lazy(() => import('./components/LevelHub'));
const GetAccess = lazy(() => import('./components/GetAccess'));
const FinishPurchase = lazy(() => import('./components/FinishPurchase'));
const Billing = lazy(() => import('./components/Billing'));
const MockSessions = lazy(() => import('./components/MockSessions'));
const MockSessionsL2 = lazy(() => import('./components/MockSessionsL2'));
const MockSessionsL3 = lazy(() => import('./components/MockSessionsL3'));
const AdminPage = lazy(() => import('./components/AdminPage'));
const AdminEventStream = lazy(() => import('./components/AdminEventStream'));
const AdminReferrals = lazy(() => import('./components/AdminReferrals'));
const AdminPartners = lazy(() => import('./components/AdminPartners'));
const RedeemSeatCode = lazy(() => import('./components/RedeemSeatCode'));
const PartnerDashboard = lazy(() => import('./components/PartnerDashboard'));
const AdminPlans = lazy(() => import('./components/AdminPlans'));
const AdminRoadmap = lazy(() => import('./components/AdminRoadmap'));
const AdminAttempts = lazy(() => import('./components/AdminAttempts'));
const AdminRanking = lazy(() => import('./components/AdminRanking'));
const AdminQuestions = lazy(() => import('./components/AdminQuestions'));
const AdminQuestionDetail = lazy(() => import('./components/AdminQuestionDetail'));
const AdminReviews = lazy(() => import('./components/AdminReviews'));
const AdminFeedback = lazy(() => import('./components/AdminFeedback'));
const AdminEmails = lazy(() => import('./components/AdminEmails'));
const AdminMigration = lazy(() => import('./components/AdminMigration'));
const SubmitQuestion = lazy(() => import('./components/SubmitQuestion'));
const EditQuestion = lazy(() => import('./components/EditQuestion'));
const EditQuestionsPage = lazy(() => import('./components/EditQuestionsPage'));

function RouteFallback() {
  return (
    <div className="route-fallback" role="status" aria-live="polite">
      <p className="lede">Loading…</p>
    </div>
  );
}

/** Loads antd theme only for screens that need it (keeps homepage main chunk lean). */
function WithAntd({ children }) {
  return (
    <Suspense fallback={<RouteFallback />}>
      <AntdProvider>{children}</AntdProvider>
    </Suspense>
  );
}

const CHUNK_RELOAD_KEY = 'cfa_chunk_reload_at';

/** Stale lazy chunk after a deploy — one silent reload fetches the new build. */
function isChunkLoadError(error) {
  const text = `${error?.name || ''} ${error?.message || ''}`;
  return /ChunkLoadError|Loading (CSS )?chunk [\w-]+ failed|Failed to fetch dynamically imported module/i.test(
    text
  );
}

class RouteErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidUpdate(prevProps, prevState) {
    // Only clear an error that was already showing — not one thrown by this very navigation.
    if (this.state.hasError && prevState.hasError && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ hasError: false, error: null });
    }
  }

  componentDidCatch(error, errorInfo) {
    console.error('Route error caught by ErrorBoundary:', error, errorInfo);
    // /pricing keeps #root hidden until Pricing commits; a failed route must still show this fallback.
    releaseStaticShell('/pricing');
    if (!isChunkLoadError(error)) return;
    try {
      const last = Number(sessionStorage.getItem(CHUNK_RELOAD_KEY) || 0);
      if (Date.now() - last > 60000) {
        sessionStorage.setItem(CHUNK_RELOAD_KEY, String(Date.now()));
        window.location.reload();
      }
    } catch {
      /* private mode — show the fallback below */
    }
  }

  render() {
    if (this.state.hasError) {
      const chunk = isChunkLoadError(this.state.error);
      return (
        <div className="page-shell">
          <main className="page-shell-main">
            <div className="route-error" role="alert">
              <h1>{chunk ? 'A new version is available' : 'This page didn’t load'}</h1>
              <p className="lede">
                {chunk
                  ? 'We shipped an update while this tab was open. Reload to continue — your saved progress is safe.'
                  : 'Something went wrong on our side. Reload to try again, or head back home.'}
              </p>
              <div className="route-error-actions">
                <button
                  type="button"
                  className="btn-primary"
                  onClick={() => window.location.reload()}
                >
                  Reload page
                </button>
                <a className="btn-ghost" href="/">
                  Back to home
                </a>
              </div>
            </div>
          </main>
        </div>
      );
    }
    return this.props.children;
  }
}

/** Clear the error on navigation so one failed route does not poison the rest. */
function RouteBoundary({ children }) {
  const { pathname } = useLocation();
  return <RouteErrorBoundary resetKey={pathname}>{children}</RouteErrorBoundary>;
}

/** Scroll to `#id` on any navigation (incl. same-page links like navbar → #purchase). */
function ScrollToHash() {
  const { hash, key, pathname } = useLocation();
  const navigationType = useNavigationType();
  const initialKeyRef = useRef(key);
  useEffect(() => {
    // Back/Forward restores the previous scroll position — don't override it
    // (the initial page load is also a POP and still needs the scroll).
    if (navigationType === 'POP' && key !== initialKeyRef.current) return undefined;
    if (!hash || hash.length < 2) return undefined;
    let id;
    try {
      id = decodeURIComponent(hash.slice(1));
    } catch {
      return undefined;
    }
    let tries = 0;
    let timer;
    const reduceMotion =
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const attempt = () => {
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
        return;
      }
      // Lazy routes / async data render the target later — retry briefly.
      tries += 1;
      if (tries < 25) timer = window.setTimeout(attempt, 120);
    };
    timer = window.setTimeout(attempt, 60);
    return () => window.clearTimeout(timer);
  }, [hash, key, pathname, navigationType]);
  return null;
}

function LegacyQuestionAnalytics() {
  const { questionId } = useParams();
  return <Navigate to={`/admin/questions/${questionId}`} replace />;
}

function LogoutRoute() {
  useEffect(() => {
    import('./firebase').then(({ auth }) => auth.signOut());
  }, []);
  return <Navigate to="/" replace />;
}

function App() {
  useEffect(() => {
    captureAcquisition();
    removeCrawlerShell();
  }, []);

  return (
    <QuizProvider>
      <Router>
        <LoadPhi />
        <ReferralCapture />
        <DeferredBoot>
          <Suspense fallback={null}>
            <PhiIdentify />
            <Presence />
            <PageViewTracker />
            <FoundingOfferPromo />
            <GoogleAuthComplete />
            <AnalyticsNotice />
            <VerifyEmailNotice />
            <FunnelFeedback />
          </Suspense>
        </DeferredBoot>
        <ScrollToHash />
        <Navbar />
        <RouteBoundary>
          <Suspense fallback={<RouteFallback />}>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/cfa-exam-uk" element={<CountryPage country="uk" />} />
              <Route path="/cfa-exam-canada" element={<CountryPage country="canada" />} />
              <Route path="/cfa-exam-singapore" element={<CountryPage country="singapore" />} />
              <Route path="/cfa-exam-hong-kong" element={<CountryPage country="hong-kong" />} />
              <Route path="/cfa-exam-australia" element={<CountryPage country="australia" />} />
              <Route path="/cfa-exam-new-zealand" element={<CountryPage country="new-zealand" />} />
              <Route path="/cfa-exam-usa" element={<CountryPage country="usa" />} />
              <Route path="/cfa-level-1-exam-fees" element={<GuidePage guide="fees" />} />
              <Route path="/cfa-practice-exam" element={<GuidePage guide="practice-exam" />} />
              <Route path="/quiz" element={<Navigate to="/quiz/session" replace />} />
              <Route path="/quiz/session" element={<MockSessions />} />
              <Route
                path="/quiz/l2/:topic"
                element={
                  <WithAntd>
                    <RequireAuth>
                      <Quiz key="l2" level="l2" />
                    </RequireAuth>
                  </WithAntd>
                }
              />
              <Route
                path="/quiz/l2"
                element={
                  <RequireAuth>
                    <MockSessionsL2 />
                  </RequireAuth>
                }
              />
              <Route
                path="/quiz/l3/:topic"
                element={
                  <WithAntd>
                    <RequireAuth>
                      <Quiz key="l3" level="l3" />
                    </RequireAuth>
                  </WithAntd>
                }
              />
              <Route
                path="/quiz/l3"
                element={
                  <RequireAuth>
                    <MockSessionsL3 />
                  </RequireAuth>
                }
              />
              <Route
                path="/quiz/:topic/:lessonSlug/:sectionSlug"
                element={
                  <WithAntd>
                    <RequireAuth>
                      <Quiz />
                    </RequireAuth>
                  </WithAntd>
                }
              />
              <Route
                path="/quiz/:topic/:lessonSlug"
                element={
                  <WithAntd>
                    <RequireAuth>
                      <Quiz />
                    </RequireAuth>
                  </WithAntd>
                }
              />
              <Route
                path="/quiz/:topic"
                element={
                  <WithAntd>
                    <RequireAuth>
                      <Quiz />
                    </RequireAuth>
                  </WithAntd>
                }
              />
              <Route path="/signup" element={<SignUp />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route
                path="/submit-question"
                element={
                  <WithAntd>
                    <RequireAdmin>
                      <SubmitQuestion />
                    </RequireAdmin>
                  </WithAntd>
                }
              />
              <Route
                path="/edit-question"
                element={
                  <WithAntd>
                    <RequireAdmin>
                      <EditQuestion />
                    </RequireAdmin>
                  </WithAntd>
                }
              />
              <Route
                path="/edit-questions"
                element={
                  <WithAntd>
                    <RequireAdmin>
                      <EditQuestionsPage />
                    </RequireAdmin>
                  </WithAntd>
                }
              />
              <Route path="/glossary" element={<Glossary />} />
              <Route path="/leaderboard" element={<Leaderboard />} />
              <Route path="/blog" element={<Blog />} />
              <Route path="/blog/:slug" element={<Blog />} />
              <Route
                path="/study-guide"
                element={
                  <WithAntd>
                    <StudyGuide />
                  </WithAntd>
                }
              />
              <Route
                path="/study-guide/*"
                element={
                  <WithAntd>
                    <StudyGuide />
                  </WithAntd>
                }
              />
              <Route
                path="/edit-profile"
                element={
                  <WithAntd>
                    <EditProfile />
                  </WithAntd>
                }
              />
              <Route
                path="/quiz-attempts"
                element={
                  <WithAntd>
                    <QuizAttempts />
                  </WithAntd>
                }
              />
              <Route
                path="/progress"
                element={
                  <WithAntd>
                    <Progress />
                  </WithAntd>
                }
              />
              <Route
                path="/dashboard"
                element={
                  <WithAntd>
                    <Progress />
                  </WithAntd>
                }
              />
              <Route path="/account" element={<Navigate to="/dashboard" replace />} />
              <Route path="/logout" element={<LogoutRoute />} />
              <Route
                path="/exam-date"
                element={
                  <WithAntd>
                    <ExamDateOnboarding />
                  </WithAntd>
                }
              />
              <Route path="/cfa-mock-exams" element={<MockExamsHub />} />
              <Route path="/redeem" element={<RequireAuth><RedeemSeatCode /></RequireAuth>} />
              <Route path="/partner" element={<PartnerDashboard />} />
              <Route path="/pricing" element={<Pricing />} />
              {/* CFA-129: Netlify 301s these too; client-side links land in the same place. */}
              <Route path="/level/1" element={<Navigate to="/" replace />} />
              <Route path="/mock-exams" element={<Navigate to="/cfa-mock-exams" replace />} />
              <Route path="/level/2" element={<LevelHub level="l2" />} />
              <Route path="/level/3" element={<LevelHub level="l3" />} />
              <Route path="/l2" element={<Navigate to="/level/2" replace />} />
              <Route path="/l3" element={<Navigate to="/level/3" replace />} />
              <Route path="/finish-purchase" element={<FinishPurchase />} />
              <Route
                path="/get-access"
                element={
                  <WithAntd>
                    <GetAccess />
                  </WithAntd>
                }
              />
              <Route path="/billing" element={<Billing />} />
              <Route
                path="/review/:token"
                element={
                  <WithAntd>
                    <ReviewAttempt />
                  </WithAntd>
                }
              />
              <Route path="/email/drill" element={<EmailDrillResult />} />
              <Route
                path="/admin"
                element={
                  <WithAntd>
                    <RequireAdmin>
                      <AdminPage />
                    </RequireAdmin>
                  </WithAntd>
                }
              />
              <Route
                path="/admin/events"
                element={
                  <WithAntd>
                    <RequireAdmin>
                      <AdminEventStream />
                    </RequireAdmin>
                  </WithAntd>
                }
              />
              <Route
                path="/admin/ranking"
                element={
                  <WithAntd>
                    <RequireAdmin>
                      <AdminRanking />
                    </RequireAdmin>
                  </WithAntd>
                }
              />
              <Route
                path="/admin/attempts"
                element={
                  <WithAntd>
                    <RequireAdmin>
                      <AdminAttempts />
                    </RequireAdmin>
                  </WithAntd>
                }
              />
              <Route
                path="/admin/questions"
                element={
                  <WithAntd>
                    <RequireAdmin>
                      <AdminQuestions />
                    </RequireAdmin>
                  </WithAntd>
                }
              />
              <Route
                path="/admin/questions/:questionId"
                element={
                  <WithAntd>
                    <RequireAdmin>
                      <AdminQuestionDetail />
                    </RequireAdmin>
                  </WithAntd>
                }
              />
              <Route
                path="/admin/reviews"
                element={
                  <WithAntd>
                    <RequireAdmin>
                      <AdminReviews />
                    </RequireAdmin>
                  </WithAntd>
                }
              />
              <Route
                path="/admin/feedback"
                element={
                  <WithAntd>
                    <RequireAdmin>
                      <AdminFeedback />
                    </RequireAdmin>
                  </WithAntd>
                }
              />
              <Route
                path="/admin/emails"
                element={
                  <WithAntd>
                    <RequireAdmin>
                      <AdminEmails />
                    </RequireAdmin>
                  </WithAntd>
                }
              />
              <Route path="/admin/partners" element={<RequireAdmin><AdminPartners /></RequireAdmin>} />
              <Route path="/admin/referrals" element={<WithAntd><RequireAdmin><AdminReferrals /></RequireAdmin></WithAntd>} />
              <Route
                path="/admin/plans"
                element={
                  <WithAntd>
                    <RequireAdmin>
                      <AdminPlans />
                    </RequireAdmin>
                  </WithAntd>
                }
              />
              <Route
                path="/admin/roadmap"
                element={
                  <WithAntd>
                    <RequireAdmin>
                      <AdminRoadmap />
                    </RequireAdmin>
                  </WithAntd>
                }
              />
              <Route
                path="/admin/migration"
                element={
                  <WithAntd>
                    <RequireAdmin>
                      <AdminMigration />
                    </RequireAdmin>
                  </WithAntd>
                }
              />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/referral-terms" element={<ReferralTerms />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/careers" element={<Careers />} />
              <Route path="/study-plan" element={<StudyPlan />} />
              <Route path="/about" element={<About />} />
              <Route path="/contact" element={<Contact />} />
              <Route
                path="/analytics/:questionId"
                element={
                  <WithAntd>
                    <RequireAdmin>
                      <LegacyQuestionAnalytics />
                    </RequireAdmin>
                  </WithAntd>
                }
              />
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </Suspense>
        </RouteBoundary>
      </Router>
    </QuizProvider>
  );
}

export default App;
